import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Validation from '../../../shared/Validation';
import MaskedInput from 'react-text-mask';
import Logout from '../../../shared/Logout/Logout';
import FloatingError from '../../../shared/FloatingError/FloatingError';
import classNames from 'classnames';

import './EnrollDoubleInput.scss';
import imprivataIcon from '../../Login/Icons/imprivata.svg';

class EnrollDoubleInput extends Component {
  static propTypes = {
    /** The header text for this enrollment screen */
    headerTitle: PropTypes.string,
    /** The placeholder text for the first input */
    serialPlaceholderText: PropTypes.string,
    /** The placeholder text for the second input */
    tokenCodePlaceholderText: PropTypes.string,
    /** The input type (password or text) for the first input */
    serialInputType: PropTypes.oneOf([
      'password', 'text', 'imprSerialNumber', 'number',
    ]),
    /** The input type (password or text) for the second input */
    tokenCodeInputType: PropTypes.oneOf([
      'password', 'text', 'number',
    ]),
    /** The value for the first input */
    serial: PropTypes.string,
    /** The value for the second input */
    tokenCode: PropTypes.string,
    /** The text to be displayed underneath the enrollment input */
    linkText: PropTypes.string,
    /** The function that is executed when the link is clicked */
    onLinkClicked: PropTypes.func,
    /** The function that is executed when the input is changed */
    onInputChange: PropTypes.func,
    /** The function that is executed when the form is submitted */
    onSubmit: PropTypes.func,
    /** The text for the submit button */
    submitText: PropTypes.string,
    /** Smaller text that is to appear between the submit button and the text inputs */
    postScript: PropTypes.string,
    /** Validation constraints object for validate.js
    (https://validatejs.org/#constraints) */
    validation: PropTypes.object,
    /** Maximum length of value in input or blank for no maximum for the first field */
    maxLengthFirstField: PropTypes.string,
    /** Maximum length of value in input or blank for no maximum for the second field */
    maxLengthSecondField: PropTypes.string,
    /** Whether the browser should attempt to autoComplete the first field */
    autoCompleteFirstField: PropTypes.bool,
    /** Whether the browser should attempt to autoComplete the second field */
    autoCompleteSecondField: PropTypes.bool,
    /** Whether the first field should automatically gain focus */
    autoFocus: PropTypes.bool,
    /** Which errors to show */
    displayErrors: PropTypes.array,
    /** The component to be used for logout */
    logoutComponent: PropTypes.object,
    /** The DOM tree/component to be used for the footer */
    footer: PropTypes.object,
  };

  static defaultProps = {
    headerTitle: 'Enroll your Imprivata ID',
    serialPlaceholderText: 'Serial Number (e.g. IMPR 1234 5678)',
    tokenCodePlaceholderText: 'Token Code (e.g. 123456)',
    serialInputType: PropTypes.text,
    tokenCodeInputType: PropTypes.password,
    linkText: 'Not now',
    submitText: 'Submit',
    validation: {
      serial: {
        presence: true,
        length: { minimum: 1, },
      },
      tokenCode: {
        presence: true,
        length: { minimum: 1, },
      },
    },
    maxLengthFirstField: '',
    maxLengthSecondField: '',
    autoCompleteFirstField: false,
    autoCompleteSecondField: false,
    autoFocus: false,
    displayErrors: [],
    logoutComponent: <Logout/>,
    onInputChange: () => void(0),
    onSubmit: () => void(0),
    serial: '',
    tokenCode: '',
    postScript: '',
  };

  constructor(props) {
    super(props);
    this.validation = new Validation(props.validation);
    this.state = {
      errors: '',
      showErrors: false,
      serial: props.serial,
      tokenCode: props.tokenCode,
      disableSubmit: true,
    };
  }

  componentDidUpdate = (prevProps) => {
    if (this.props.tokenCode !== prevProps.tokenCode) {
      this.setState({ tokenCode: this.props.tokenCode, });
    }
    if (this.props.serial !== prevProps.serial) {
      this.setState({ serial: this.props.serial, });
    }
  };

  changeTokenInput = (event) => {
    let tokenCode = event.target.value.trim();
    const maxlength = parseInt(this.props.maxLengthSecondField, 10);
    if (!isNaN(maxlength) ) {
      tokenCode = tokenCode.slice(0, maxlength);
    }
    if (this.props.tokenCodeInputType === 'number') {
      const isPositiveInteger = RegExp('^[0-9]*$').test(tokenCode);
      if (!isPositiveInteger) {
        event.preventDefault();
        return;
      }
    }
    this.changeInput({
      serial: this.state.serial,
      tokenCode,
    });
  };

  changeSerialInput = (event) => {
    let serial = event.target.value.trim();
    const maxlength = parseInt(this.props.maxLengthFirstField, 10);
    if (!isNaN(maxlength) ) {
      serial = serial.slice(0, maxlength);
    }
    if (this.props.serialInputType === 'number') {
      const isPositiveInteger = RegExp('^[0-9]*$').test(serial);
      if (!isPositiveInteger) {
        event.preventDefault();
        return;
      }
    }
    this.changeInput({
      serial,
      tokenCode: this.state.tokenCode,
    });
  };

  changeInput = (inputValues) => {
    this.validation.updateValues(inputValues);
    inputValues.disableSubmit = this.validation.hasErrors();
    inputValues.errors = this.validation.errorMessages();
    inputValues.showErrors = false;
    this.setState(inputValues);
    this.props.onInputChange(inputValues);
  };

  submitForm = (event) => {
    event.preventDefault();
    if (this.state.errors) {
      this.setState({ showErrors: true, });
    }
    else {
      this.setState({ showErrors: false, });
      this.props.onSubmit(this.state);
    }
  };

  render() {
    const formClasses = classNames({
      'form-control': true,
      'form-error-outline': this.props.displayErrors.length > 0,
    });
    return (
      <div className='CIDRA'>
        {this.props.logoutComponent}
        <div className='login-container'>
          <form id='EnrollDoubleInput_form' className='form' onSubmit={this.submitForm}>
            <div className='form-group login-header'>
              {
                this.props.headerTitle ? (
                  <h2 id='EnrollDoubleInput_header_title' className='header-title'>
                    {this.props.headerTitle}
                  </h2>
                ) : null
              }
            </div>
            <div className='form-group instructions-ordered'>
              {this.props.children}
            </div>
            {this.props.displayErrors.length > 0 ?
              (
                <div>
                  <FloatingError messages={this.props.displayErrors}/>
                </div>
              )
              : null
            }
            <div className='form-group'>
              {
                this.props.serialInputType === 'imprSerialNumber' ? (
                  <MaskedInput
                    mask={[
                      'I', 'M', 'P', 'R', ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/,
                    ]}
                    guide={false}
                    autoFocus={this.props.autoFocus}
                    id='EnrollDoubleInput_serial_input'
                    type='text'
                    className={formClasses}
                    placeholder={this.props.serialPlaceholderText}
                    name='serialInput'
                    onChange={this.changeSerialInput}
                    autoComplete={this.props.autoCompleteFirstField ? 'on' : 'off'}
                    value={this.state.serial}
                  />
                ) : (
                  <input
                    autoFocus={this.props.autoFocus}
                    id='EnrollDoubleInput_serial_input'
                    type={this.props.serialInputType === 'number' ? 'text' : this.props.serialInputType}
                    className={formClasses}
                    placeholder={this.props.serialPlaceholderText}
                    name='serialInput'
                    onChange={this.changeSerialInput}
                    autoComplete={this.props.autoCompleteFirstField ? 'on' : 'off'}
                    value={this.state.serial}
                  />
                )
              }
            </div>
            <div className='form-group'>
              <input
                id='EnrollDoubleInput_tokencode_input'
                type={this.props.tokenCodeInputType === 'number' ? 'text' : this.props.tokenCodeInputType}
                className={formClasses}
                placeholder={this.props.tokenCodePlaceholderText}
                name='tokenCodeInput'
                ref={(input) => {
                  this.tokenCodeInput = input;
                }}
                onChange={this.changeTokenInput}
                autoComplete={this.props.autoCompleteSecondField ? 'on' : 'off'}
                value={this.state.tokenCode}
              />
            </div>
            { this.props.serialInputType === 'imprSerialNumber' ?
              (<div className='postScript-text'>
                {this.props.postScript}
              </div>) :
              ('')
            }
            <div className='form-group'>
              <input
                id='EnrollDoubleInput_submit_button'
                className='btn btn-primary btn-block'
                type='submit'
                value={this.props.submitText}
                disabled={this.state.disableSubmit}
                onClick={this.submitForm}
              />
            </div>
            {
              this.props.linkText ? (
                <div className='text-center'>
                  {
                    // eslint-disable-next-line
                  }<a id='EnrollDoubleInput_link' className='btn btn-link'
                    onClick={this.props.onLinkClicked}>
                    {this.props.linkText}
                  </a>
                </div>
              ) : null
            }
          </form>
          { this.props.footer }
          <hr className='Footer-hr'/>
          <img className='Footer' src={imprivataIcon} alt='logo'/>
        </div>
      </div>
    );
  }
}

export default EnrollDoubleInput;
